<template>
<div class="chapter">
<div class="inner_page">
<h1 class="cn"><a href="#/MoonLookedDown_toc#chap-1"><em>Chapter One</em></a></h1>
<p class="cotx"><span class="dropcap">S</span><span class="small">OPHIE</span> H<span class="small">ELLER HURRIED</span> down the busy street, oblivious to the hustle and bustle, the cars and trucks, the daily life of Victory. Her eyes passed
         from one sight to the next quickly, not alighting on anything or anyone for long as she kept moving ever forward. She could
         not, would not be late!
      </p>
<p>Above her, the summer sun pounded down mercilessly. Rain had been promised for days, but those promises had proven to be every
         bit as empty as the cloudless afternoon sky. This day was a scorcher if there ever was one! Red, white, and blue American
         flags hung limply from every storefront, stirred by only the slightest of breezes. Pushing one errant strand of hair from
         her eyes, Sophie wiped the back of her hand across her brow. Her simple blue blouse clung tightly to her skin, the fabric
         as wet as if it had just been pulled from the wash.
      </p>
<p>“Good afternoon, Sophie,” a woman’s voice called to her from somewhere near the bakery.</p>
<p>She gave no response save a nod before hurrying on, clutching her purse tightly to her chest, aware that whoever had spoken
         to her would find her behavior rude. Still, her concern was not great enough for her to stop. She had a task that would afford
         no wait, and, moments later, she finally reached her destination.
      </p>
<p>Ambrose Hardware sat in a long, thin brick building on a corner of Main Street, right next door to the grocer. With its wide
         display windows, crisp sign, and long awning, Robert Ambrose’s business was as familiar a sight to the town’s residents as
         Marge’s Diner, McKenzie’s Barber Shop, or the post office, and every bit as vital. Victory’s lone hardware store served everyone
         from the town’s most venerable families to the newest arrival.
      </p>
<p>Stepping into the welcome shade provided by the awning, Sophie looked through the large show window. Past the stenciled lettering
         on the glass, she could see hammers, saws, several buckets of paint, and a pair of shovels, but it was the centerpiece of
         the display that grabbed her attention, sending a chill racing across her skin, even on such a hot summer day.
      </p>
<p>Two large posters stood side by side on a pair of chairs, their bright headlines shouting a clear message to every passerby.
         The first showed a smiling man holding a treasury bond in one hand under the banner of:
      </p>
<p class="ext"><span class="small">WANTED—FIGHTING DOLLARS</span></p>
<p class="ext1"><span class="small">MAKE EVERY PAY DAY BOND DAY</span></p>
<p>The second poster was far more sinister-looking than its companion. On it, the dark eyes of a German soldier looked out from
         under the brim of an iron helmet, his steely gaze clearly showing his harmful intentions. The poster read:
      </p>
<p class="ext4"><span class="small">HE’S WATCHING YOU</span></p>
<p>Sophie had seen these posters and others like them in the windows of stores and homes across town, attached to lampposts and
         telephone poles, and even in the back window of a pickup truck. While she shared their sentiment as a proud American, she
         couldn’t help but worry that the more sinister poster had it backward; she and her German family were the ones being watched!
      </p>
<p>Faintly, Sophie caught her own reflection looking back at her in the glass. She looked haggard, bone-tired from lack of sleep.
         She hadn’t bothered to put on any makeup or do a thing with her hair. To do so had suddenly seemed so unimportant, so trivial!
         But she certainly looked a mess. Gingerly, she rubbed the swollen knot at her temple; the slightest touch was enough to send
         sharp ripples of pain racing across her head.
      </p>
<p>Now, even a week after the burning of her family’s barn, the wound was still angry and tender. She hadn’t so much as seen
         the blow coming! When the gunman brought the butt of his rifle down upon her skull, the brief flare of pain had not been as
         bad as the pounding headaches that had plagued her for days afterward. Thankfully, the wound was hidden in her hairline, far
         enough out of sight to keep anyone from asking any questions.
      </p>
<p>As she stood before the hardware store, Sophie knew that the physical pain she had suffered was nothing compared to the shame
         and hurt she still felt at the indignities that had been visited upon her family. These wounds were deeper and more difficult
         to heal. She wondered if their scars would ever truly vanish. Taking a deep breath, she pushed open the door.
      </p>
<p>Sophie entered Ambrose Hardware to the sound of small bells ringing above the door. The blistering heat of the summer day
         followed her inside as if it were a shawl wrapped around her shoulders. After the bright glare of outdoors, it took a moment
         for her eyes to adjust to the store’s darkened interior.
      </p>
<p>The store was crowded with goods in every nook and cranny; pots and pans, spools of thread, wire and rope, buckets of paint,
         and tin pails full of nails. Small compartments ran the length of the nearer wall, rising from the worn wooden floor to the
         high tin ceiling, their treasures hidden behind the smoky dark glass of each drawer. The very air carried with it the scent
         of available goods—oils, soaps, and wood—all gently pushed by the ceiling fans that turned lazily above her head.
      </p>
<p>“Afternoon, Miss Heller.”</p>
<p>Robert Ambrose stepped from the store’s stockroom, wiping his hands upon the dark vest that covered his button-down shirt
         and matching tie. Though Sophie felt certain that the store owner was nearly as old as her father, he carried himself like
         a much younger man. He was betrayed only by the slightest of hints to his true age; the silver hairs that were sprinkled across
         his black mane, the round, wire-rimmed glasses that sat high upon his thin nose, and the deep wrinkles that spread from around
         his mouth when he smiled all spoke of a man in his early fifties. Of medium height and build, he had a reputation about town
         as a bit of a teetotaler; no tobacco stained his long fingers or teeth and he would never be found staggering from a tavern
         as the morning sun broke the horizon. Decent and hardworking, he had built his business with his own two hands.
      </p>
<p>“Good afternoon to you, Mr. Ambrose,” she answered.</p>
<p>“Still blazing away?” He nodded toward the front door.</p>
<p>“I’m afraid so.”</p>
<p>“I swear it’s not fit for man or beast out there,” he remarked. “It gets a mite lonely in here when folks aren’t brave enough
         to venture outside on days like this. Between the war and this darn heat, I’m going to have a bear of a time this year!”
      </p>
<p>“Did you have enough time to go over my father’s list?” Sophie asked, a bit too anxious to engage in talk of the weather.
         “I know you asked me to come back today, but if you need more time—”
      </p>
<p>“No, my dear, it’s fine,” Mr. Ambrose said as he slid a pencil out from behind one ear and consulted the list she had left
         with him earlier in the week. “I’ve managed to round up most of what your father was asking for, all except for the roofing
         pitch. That will have to be ordered from Springfield, but it shouldn’t take more than a week at best.”
      </p>
<p>“He’s certainly grateful for all that you can do.”</p>
<p>“Your father’s been coming here from the first day you and yours set foot in town.” The hardware store owner smiled. “Darn
         shame losing a building like that, but I’ll do all I can to get him back on his feet.”
      </p>
<p>“He’ll be glad—”</p>
<p>“Gosh darn it all,” the man suddenly blurted, snapping his fingers across the piece of paper with a crack. “I forgot all about
         those hammers! I best check and make sure I don’t…” He mumbled to himself as he disappeared back into the inky darkness of
         the storeroom.
      </p>
<p>As much as it shamed her to admit it, Sophie was thankful that Mr. Ambrose had left her when he did; casual talk of what had
         happened on that fateful night made her skin crawl. Remembering the flames that had stretched toward the night sky, the sounds
         of the barn collapsing, the armed and hooded men, and the dark crimson of her father’s blood as she cradled his head in her
         arms still made her tremble. She knew that she should go forward with her life, leave the past in the past, but she found
         it too hard.
      </p>
<p>Her father had no such trouble. The morning after their lives had been forever changed, Hermann Heller had been out picking
         through the still smoldering wreckage of the barn, his head swathed in thick cloth bandages. Within days, he’d written a list
         of things he would need to reconstruct what they had lost. He would harbor no talk of the police or of following the advice
         given to them by the hooded men to leave their home, to leave Victory behind. Whenever he was asked about that night, he became
         upset, his voice quickly rising in anger. Sophie could not understand her father’s defiance; she could not stop asking the
         questions her father refused to give voice.
      </p>
<p><em>Who were the hooded men? Why had they committed such acts?</em></p>
<p>These questions haunted Sophie every bit as deeply as the events of that night. From the moment she woke every morning to
         the instant she fell asleep at night, she thought of the men who had hurt her family. She waited, fearful that they would
         return to make good on their horrific promise, but every night their failure to reappear made her all the more fearful. Her
         dreams had also been poisoned; gone were the visions of wildflowers, replaced by biting laughter and hungry flames.
      </p>
<p>Even in this store, Sophie’s eyes sought out the hated men, straining to find them hiding behind the stacks of washpans or
         for sight of their reflections in the dark glass of the wall of compartments. To her, they potentially lurked behind every
         corner, across every street, and even within every nod, smile, or stare that came her way.
      </p>
<p><em>They could be anyone… anywhere.</em></p>
<p>Her thoughts were broken by the ringing of bells as the door to the store was opened and a pair of men entered, one of them
         nearly as large as one of Mr. Ambrose’s display cases, the other as reed-thin as a broom. Sophie immediately recognized them
         and smiled brightly.
      </p>
<p>Charley Tatum was nearly as wide as he was tall, with a personality that was more than a match for his girth. A farmer who
         worked the land just south of Victory’s limits, he was a familiar sight to the town’s inhabitants. He was quick to laugh,
         and his voice boomed over every conversation he joined and filled the church with a deep baritone every Sunday morning. Dressed
         in dark overalls, he wiped his sweat-beaded brow and flushed red cheeks with a handkerchief before burying it deep in his
         pockets.
      </p>
<p>“Well, I do declare, Miss Sophie!” he bellowed, breaking into a toothy grin. “You get more lovely just about every time I
         see you! ’Fore long, the simple folks of this here town are gonna see you on one of them there movie posters!”
      </p>
<p>“My mother says it’s not nice to tease,” she chided him.</p>
<p>“Who’s teasin’? Why, there ain’t but nothin’ any of them Hollywood beauties has got on you! Not that Betty Grable, Ginger
         Rogers, Marlene Dietrich, nor that Vivien Leigh neither!”
      </p>
<p>“My pa ain’t lyin’, that’s for sure,” Will Tatum offered in defense of his father’s proclamation. Unlike his larger-than-life
         father, Will was timid and skinny, still boyish in many ways though he was nearly a man. His personality was just the opposite
         of Charley’s, his voice almost a whisper spit in the face of a storm.
      </p>
<p>“Then you’re <em>both</em> teasing me!”
      </p>
<p>“Am I going to have a problem with you harassing my customers?” Mr. Ambrose kidded as he stepped from the storeroom. His eyes
         rose from her father’s list just long enough for the businessman to give Sophie a knowing wink. “I’d hate to have to call
         the sheriff.”
      </p>
<p>“I swear this might be the first time I ever heard of a woman complainin’ about a man sayin’ she was pretty,” Charley cackled,
         slapping one wide hand upon his knee. Leaning back, he rested his weight against one of the long display cases. Sophie felt
         sure that she could hear the old wood groan with the load. “What is the world comin’ to?”
      </p>
<p>To that, they all laughed heartily.</p>
<p>“I was mighty sorry to hear what happened to your barn,” Charley offered in a more somber tone. “With a tipped-over lantern,
         it’ll only take a few shakes of a sow’s tail ’fore the whole thing is ablaze. That happens, ain’t but nothin’ you can do but
         stand back, watch, and pray for rain.”
      </p>
<p>“And we sure ain’t gettin’ much of that,” Will added.</p>
<p>“We should all be a mite extra careful this time of year,” Robert said as he wiped his glasses on his apron. “As much as I
         appreciate the business, it’s a shame to lose a building like that.”
      </p>
<p>“An outright shame,” Charley echoed.</p>
<p>Sophie nodded solemnly. The story that had been given, the <em>outright lie</em> that had been created, was that Hermann had accidentally tipped over a lantern when checking upon their animals. Before he
         could hope to contain it, the fire had raced out of control. The entire Heller family had agreed that this was the story they
         would tell, but Sophie still felt the shame of their untruth coloring her cheeks.
      </p>
<p>“I appreciate your kind words.” She smiled at them. “But my father says that we shouldn’t feel sorry about what happened.
         We just need to roll up our sleeves and get back to work making it all right again.”
      </p>
<p>“That’s the spirit!” Charley exclaimed. “Why, if I know Hermann, he’ll have that barn back up so fast that you’ll wonder if
         you imagined that pesky fire! It’ll look better than new, what with a fresh coat of paint and all!”
      </p>
<p>“I wouldn’t doubt that for a minute,” Robert Ambrose agreed.</p>
<p>“And you make sure to tell Hermann that if he needs any help gettin’ that barn of his back together, he shouldn’t hesitate
         to ask us,” Charley offered, patting his son so heavily on his shoulder that the boy’s knees sagged. “We ain’t against hard
         work to help out a neighbor. Ain’t that right, boy?”
      </p>
<p>“Darn right, Pa!” Will agreed.</p>
<p>“After all, an accident like that could have happened to anyone. ‘There but for the grace of God’ and all. I sure ain’t the
         most graceful man alive,” the large farmer chuckled, patting his ample stomach. “It could just have easy been me knocking
         over that lantern and you can bet a helping of my Martha’s biscuits that I wouldn’t have hesitated a second to ask for a touch
         of help.”
      </p>
<p>While Sophie appreciated Charley Tatum’s sentiment, that it could have been any of Victory’s many farmers who had lost their
         barn, she knew that his words rang hollow. What had happened a week before would only have happened to them.
      </p>
<p><em>Because we came from Germany…</em></p>
<p>“Thank… thank you, Mr. Tatum,” Sophie stammered, forcing a smile. “I’ll make sure to tell my father of your offer.”</p>
<p>“You do that.” He nodded. “Anything at all, just let us know.”</p>
<p>After making arrangements with Mr. Ambrose for her father and brother to pick up the supplies they would need to rebuild the
         barn and saying her good-byes to the Tatums, Sophie hurried from the hardware store and again stood under the awning. She
         gulped the hot air as waves of heat rose from the pavement. She knew that she should begin the long trek home but her feet
         would not move. A persistent, gnawing fear had grabbed her. Stomach-churning thoughts flashed across her head too worrisome
         to ignore.
      </p>
<p>Could Charley Tatum know anything about what had happened to their barn? Could Mr. Ambrose? Was it possible that they were
         involved? While Charley undoubtedly would have stood out like a sore thumb, would she have recognized Will if he had been
         one of the men? What if—
      </p>
<p>Struggling with the effort, Sophie put a stop to her panicked thoughts. Her breathing had started to come in fits and her
         heart pounded hard in her chest like a jackrabbit’s. What was she thinking? The very idea of such things was sheer madness!
         Would she wonder about everyone she knew? Would she look at friends differently? Was hurtful intent hiding behind every face
         or kind word, even those of the people she had known nearly all her life?
      </p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// MoonLookedDown_chap-1.vue?93a4526a